import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { 
  FileText, 
  Image, 
  Video, 
  Award,
  BookOpen,
  Folder,
  User,
  ExternalLink,
  Calendar,
  Download,
  Eye,
  GraduationCap,
  Star
} from "lucide-react";
import edugogyLogo from "@/assets/edugogy-logo.png";

interface Portfolio {
  id: string;
  title: string;
  description: string;
  created_at: string;
  updated_at: string;
}

interface PortfolioFolder {
  id: string;
  name: string;
  color: string;
  position: number;
}

interface PortfolioArtifact {
  id: string;
  title: string;
  description: string;
  file_url: string;
  file_type: string;
  file_size: number;
  folder_id?: string;
  artifact_order: number;
  created_at: string;
  metadata?: any;
}

interface PortfolioBadge {
  id: string;
  badge_id: string;
  display_order: number;
  earned_date: string;
  is_public: boolean;
}

interface PortfolioCertificate {
  id: string;
  certificate_id: string;
  display_order: number;
  is_public: boolean;
}

export default function SharedPortfolio() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [folders, setFolders] = useState<PortfolioFolder[]>([]);
  const [artifacts, setArtifacts] = useState<PortfolioArtifact[]>([]);
  const [badges, setBadges] = useState<PortfolioBadge[]>([]);
  const [certificates, setCertificates] = useState<PortfolioCertificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openFolder, setOpenFolder] = useState<PortfolioFolder | null>(null);
  const [viewingLessonPlan, setViewingLessonPlan] = useState<PortfolioArtifact | null>(null);

  useEffect(() => {
    if (shareToken) {
      loadSharedPortfolio();
    }
  }, [shareToken]);

  const loadSharedPortfolio = async () => {
    try {
      setLoading(true);
      
      // Load portfolio by share token
      const { data: portfolioData, error: portfolioError } = await supabase
        .rpc('get_public_portfolio_by_token', { p_share_token: shareToken });

      if (portfolioError || !portfolioData || portfolioData.length === 0) {
        setError('Portfolio not found or is not publicly shared');
        return;
      }

      const portfolio = portfolioData[0];
      setPortfolio(portfolio);

      // Load all portfolio data in parallel
      const [foldersResult, artifactsResult, badgesResult, certificatesResult] = await Promise.all([
        // Load folders with public access via share token
        supabase
          .from('portfolio_folders')
          .select('*')
          .eq('portfolio_id', portfolio.id)
          .order('position'),
        
        // Load artifacts with public access via share token  
        supabase
          .from('portfolio_artifacts')
          .select('*')
          .eq('portfolio_id', portfolio.id)
          .order('artifact_order'),
        
        // Load badges
        supabase
          .from('portfolio_badges')
          .select('*')
          .eq('portfolio_id', portfolio.id)
          .eq('is_public', true)
          .order('display_order'),
        
        // Load certificates
        supabase
          .from('portfolio_certificates')
          .select('*')
          .eq('portfolio_id', portfolio.id)
          .eq('is_public', true)
          .order('display_order')
      ]);

      setFolders(foldersResult.data || []);
      setArtifacts(artifactsResult.data || []);
      setBadges(badgesResult.data || []);
      setCertificates(certificatesResult.data || []);

    } catch (error) {
      console.error('Error loading shared portfolio:', error);
      setError('Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <Image className="h-5 w-5 text-blue-500" />;
    if (fileType.startsWith('video/')) return <Video className="h-5 w-5 text-red-500" />;
    if (fileType === 'lesson_plan') return <GraduationCap className="h-5 w-5 text-purple-500" />;
    return <FileText className="h-5 w-5 text-green-500" />;
  };

  const handleArtifactClick = (artifact: PortfolioArtifact) => {
    if (artifact.file_type === 'lesson_plan') {
      setViewingLessonPlan(artifact);
    } else if (artifact.file_url) {
      window.open(artifact.file_url, '_blank');
    }
  };

  const handleOpenFolder = (folder: PortfolioFolder) => {
    setOpenFolder(folder);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto p-6">
          {/* Header with logo */}
          <div className="text-center py-8 mb-8">
            <img 
              src={edugogyLogo} 
              alt="Edugogy" 
              className="mx-auto mb-6 h-16 object-contain"
            />
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-96 mx-auto"></div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !portfolio) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <img 
            src={edugogyLogo} 
            alt="Edugogy" 
            className="mx-auto mb-8 h-16 object-contain"
          />
          <Card className="max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              <User className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <h2 className="text-2xl font-semibold mb-2 text-gray-800">Portfolio Not Found</h2>
              <p className="text-gray-600">
                {error || 'The portfolio you\'re looking for doesn\'t exist or is not publicly shared.'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const unfiledArtifacts = artifacts.filter(a => !a.folder_id);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="container mx-auto p-6 space-y-8">
        {/* Header with Edugogy Logo */}
        <div className="text-center py-8 bg-white rounded-2xl shadow-sm border">
          <img 
            src={edugogyLogo} 
            alt="Edugogy" 
            className="mx-auto mb-6 h-16 object-contain"
          />
          <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {portfolio.title}
          </h1>
          <p className="text-xl text-gray-600 mb-6 max-w-2xl mx-auto">{portfolio.description}</p>
          <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Created {new Date(portfolio.created_at).toLocaleDateString()}
            </div>
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Public Portfolio
            </div>
          </div>
        </div>

        {/* Portfolio Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
            <CardContent className="p-4 text-center">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-80" />
              <div className="text-2xl font-bold">{artifacts.length}</div>
              <div className="text-sm opacity-80">Artifacts</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-500 to-orange-500 text-white border-0">
            <CardContent className="p-4 text-center">
              <Award className="h-8 w-8 mx-auto mb-2 opacity-80" />
              <div className="text-2xl font-bold">{badges.length}</div>
              <div className="text-sm opacity-80">Badges</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-emerald-500 text-white border-0">
            <CardContent className="p-4 text-center">
              <GraduationCap className="h-8 w-8 mx-auto mb-2 opacity-80" />
              <div className="text-2xl font-bold">{certificates.length}</div>
              <div className="text-sm opacity-80">Certificates</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-indigo-500 text-white border-0">
            <CardContent className="p-4 text-center">
              <Folder className="h-8 w-8 mx-auto mb-2 opacity-80" />
              <div className="text-2xl font-bold">{folders.length}</div>
              <div className="text-sm opacity-80">Folders</div>
            </CardContent>
          </Card>
        </div>

        {/* Badges Section */}
        {badges.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Award className="h-6 w-6 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-800">Badges & Awards</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {badges.map(badge => (
                <Card key={badge.id} className="hover:shadow-md transition-shadow border-0 bg-gradient-to-br from-yellow-50 to-orange-50">
                  <CardContent className="p-4 text-center">
                    <div className="h-16 w-16 mx-auto mb-3 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                      <Star className="h-8 w-8 text-white" />
                    </div>
                    <p className="font-semibold text-gray-800 text-sm">Badge Achievement</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Earned {new Date(badge.earned_date).toLocaleDateString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Certificates Section */}
        {certificates.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-green-100 rounded-lg">
                <GraduationCap className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-800">Certificates</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {certificates.map(cert => (
                <Card key={cert.id} className="hover:shadow-md transition-shadow border-0 bg-gradient-to-br from-green-50 to-emerald-50">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="h-14 w-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-lg">
                        <BookOpen className="h-7 w-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800 mb-1">
                          Professional Certificate
                        </p>
                        <p className="text-sm text-gray-600 mb-2">
                          Certificate #{cert.certificate_id}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Calendar className="h-3 w-3" />
                          Professional Development
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Folders Section */}
        {folders.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Folder className="h-6 w-6 text-purple-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-800">Organized Collections</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {folders.map(folder => {
                const folderArtifacts = artifacts.filter(a => a.folder_id === folder.id);
                return (
                  <Card 
                    key={folder.id} 
                    className="hover:shadow-md transition-shadow border-0 bg-gradient-to-br from-gray-50 to-purple-50 cursor-pointer"
                    onClick={() => handleOpenFolder(folder)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <Folder style={{ color: folder.color }} className="h-6 w-6" />
                        <span className="font-semibold text-gray-800 text-sm truncate">{folder.name}</span>
                      </div>
                      <div className="text-center py-4">
                        <div className="text-2xl font-bold text-gray-600">{folderArtifacts.length}</div>
                        <div className="text-xs text-gray-500">items</div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Artifacts Section */}
        {unfiledArtifacts.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-800">Portfolio Artifacts</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {unfiledArtifacts.map(artifact => (
                <Card key={artifact.id} className="hover:shadow-md transition-shadow border-0 bg-gradient-to-br from-blue-50 to-indigo-50">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        {getFileIcon(artifact.file_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 truncate mb-1">{artifact.title}</p>
                        <p className="text-sm text-gray-600 line-clamp-2">{artifact.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(artifact.created_at).toLocaleDateString()}
                      </div>
                      <span>{formatFileSize(artifact.file_size)}</span>
                    </div>
                    {artifact.file_url && (
                      <Button 
                        size="sm" 
                        className="w-full mt-3 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                        onClick={() => handleArtifactClick(artifact)}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        View Artifact
                      </Button>
                    )}
                    {artifact.file_type === 'lesson_plan' && (
                      <Button 
                        size="sm" 
                        className="w-full mt-3 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
                        onClick={() => handleArtifactClick(artifact)}
                      >
                        <GraduationCap className="h-3 w-3 mr-1" />
                        View Lesson Plan
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {artifacts.length === 0 && badges.length === 0 && certificates.length === 0 && (
          <Card className="text-center py-16 bg-white rounded-2xl shadow-sm border">
            <CardContent>
              <User className="h-20 w-20 mx-auto mb-4 text-gray-400" />
              <h3 className="text-2xl font-semibold mb-3 text-gray-800">Portfolio In Progress</h3>
              <p className="text-gray-600 max-w-md mx-auto">
                This portfolio is just getting started. Check back later to see artifacts, badges, and certificates.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center pt-8 border-t border-gray-200 bg-white rounded-2xl shadow-sm">
          <div className="p-6">
            <img 
              src={edugogyLogo} 
              alt="Edugogy" 
              className="mx-auto mb-3 h-8 object-contain opacity-60"
            />
            <p className="text-sm text-gray-500">
              Powered by Edugogy • Professional Development Portfolio Platform
            </p>
          </div>
        </div>

        {/* Folder View Dialog */}
        <Dialog open={!!openFolder} onOpenChange={() => setOpenFolder(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Folder style={{ color: openFolder?.color }} className="h-5 w-5" />
                {openFolder?.name}
              </DialogTitle>
              <DialogDescription>
                Portfolio artifacts in this folder
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {openFolder && (() => {
                const folderArtifacts = artifacts.filter(a => a.folder_id === openFolder.id);
                
                if (folderArtifacts.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <Folder className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium">This folder is empty</p>
                      <p className="text-sm">No public artifacts to display</p>
                    </div>
                  );
                }
                
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {folderArtifacts.map(artifact => (
                      <Card 
                        key={artifact.id} 
                        className="hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => handleArtifactClick(artifact)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
                              {getFileIcon(artifact.file_type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{artifact.title}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {artifact.description}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">
                              {new Date(artifact.created_at).toLocaleDateString()}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              Click to view
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                );
              })()}
            </div>
          </DialogContent>
        </Dialog>

        {/* Lesson Plan Viewer Dialog */}
        <Dialog open={!!viewingLessonPlan} onOpenChange={() => setViewingLessonPlan(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-primary" />
                {viewingLessonPlan?.title}
              </DialogTitle>
              <DialogDescription>
                {viewingLessonPlan?.description}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {viewingLessonPlan && viewingLessonPlan.metadata?.lesson_plan_data && (() => {
                const lessonData = viewingLessonPlan.metadata.lesson_plan_data;
                
                return (
                  <>
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/20 rounded-lg">
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Subject</Label>
                        <p className="text-sm font-medium">{lessonData.subject}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Grade Level</Label>
                        <p className="text-sm font-medium">{lessonData.grade_level}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Duration</Label>
                        <p className="text-sm font-medium">{lessonData.duration} minutes</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-muted-foreground">Created</Label>
                        <p className="text-sm font-medium">
                          {new Date(lessonData.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {/* Learning Objectives */}
                    {lessonData.objectives && lessonData.objectives.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold mb-3">Learning Objectives</h3>
                        <ul className="space-y-2">
                          {lessonData.objectives.map((objective: string, index: number) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="text-primary font-semibold">•</span>
                              <span className="text-sm">{objective}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Activities */}
                    {lessonData.activities && lessonData.activities.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold mb-3">Activities</h3>
                        <div className="space-y-4">
                          {lessonData.activities.map((activity: any, index: number) => (
                            <Card key={index} className="p-4">
                              <div className="flex items-start gap-3">
                                <div className="h-8 w-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-semibold text-primary">{index + 1}</span>
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h4 className="font-semibold">{activity.name}</h4>
                                    <span className="text-xs px-2 py-1 bg-muted rounded-full">
                                      {activity.duration} min
                                    </span>
                                  </div>
                                  <p className="text-sm text-muted-foreground">{activity.description}</p>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}